import { useCallback, useEffect, useState } from 'react';
import { fetchPayments } from './api';
import { defaultFilters, type PaymentFilters, type PaymentListResponse } from './types';

/**
 * Loads the payment list on entry and re-fetches when the filters/date-range
 * change (PRD §2/§7). No background poll — operator monitoring data is not
 * live-ticking; refresh is explicit (re-filter, Show more, or Retry).
 *
 * Error model (PRD §9): a failed initial load OR a failed filter/range change
 * (both replace the body) enters the `error` phase. A failed "Show more"
 * (append) is a transient action error — it keeps the existing rows and
 * rethrows so the caller can toast (LESSONS → toast vs banner). Filter state
 * lives here (in-memory): it survives in-app navigation to the detail screen
 * and back, and resets to the current-month default on a full refresh (O-12).
 */
export type ListPhase = 'loading' | 'ready' | 'error';

export type PaymentListState = {
  phase: ListPhase;
  data: PaymentListResponse | null;
  filters: PaymentFilters;
  /** Apply a partial filter change (resets to page 1) and re-fetch. */
  applyFilters: (partial: Partial<PaymentFilters>) => void;
  /** Append the next page; rejects on failure so the caller can toast. */
  loadMore: () => Promise<void>;
  /** Re-issue the current query (Retry). */
  reload: () => void;
};

export const usePaymentList = (language: string): PaymentListState => {
  const [filters, setFilters] = useState<PaymentFilters>(defaultFilters);
  const [phase, setPhase] = useState<ListPhase>('loading');
  const [data, setData] = useState<PaymentListResponse | null>(null);

  const load = useCallback(
    async (next: PaymentFilters, append: boolean): Promise<void> => {
      if (!append) setPhase('loading');
      try {
        const res = await fetchPayments(next, language);
        setData((prev) => (append && prev ? { ...res, data: [...prev.data, ...res.data] } : res));
        setPhase('ready');
      } catch (error) {
        if (append) throw error; // keep current rows; caller toasts
        setData(null);
        setPhase('error');
      }
    },
    [language],
  );

  useEffect(() => {
    void load(defaultFilters, false);
  }, [load]);

  const applyFilters = useCallback(
    (partial: Partial<PaymentFilters>) => {
      const next = { ...filters, ...partial, page: 1 };
      setFilters(next);
      void load(next, false);
    },
    [filters, load],
  );

  const loadMore = useCallback(async (): Promise<void> => {
    const next = { ...filters, page: filters.page + 1 };
    setFilters(next);
    await load(next, true);
  }, [filters, load]);

  const reload = useCallback(() => {
    void load(filters, false);
  }, [filters, load]);

  return { phase, data, filters, applyFilters, loadMore, reload };
};
